import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Users, ArrowsClockwise, CheckCircle, Copy } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { StatusBadge } from "@/components/StatusBadge";
import { RoomTypePhotos } from "@/components/RoomTypePhotos";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const ROOM_IMAGES = [
  "https://images.unsplash.com/photo-1629140727571-9b5c6f6267b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxjbGVhbiUyMG1vZGVybiUyMGhvdGVsJTIwcm9vbXxlbnwwfHx8fDE3ODgwMTMyNDV8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxjbGVhbiUyMG1vZGVybiUyMGhvdGVsJTIwcm9vbXxlbnwwfHx8fDE3ODgwMTMyNDV8MA&ixlib=rb-4.1.0&q=85",
];

const EMPTY = { name: "", type: "Standard", rate: "", capacity: 2, status: "available", description: "", external_id: "", auto_relist: false };
const TYPES = ["Standard", "Deluxe", "Suite", "Family Suite", "Dormitory"];
const STATUSES = ["available", "occupied", "cleaning", "maintenance"];

export default function Rooms() {
  const [rooms, setRooms] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { fmt, settings } = useCurrency();
  const easystayConnected = !!settings?.easystay_property_id;

  const load = () => api.get("/rooms").then(({ data }) => setRooms(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (room) => { setEditing(room); setForm({ ...room }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, rate: parseFloat(form.rate), capacity: parseInt(form.capacity, 10) };
      if (editing) await api.put(`/rooms/${editing.id}`, payload);
      else await api.post("/rooms", payload);
      toast.success(editing ? "Room updated" : "Room added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (room) => {
    if (!window.confirm(`Delete room ${room.name}?`)) return;
    try {
      await api.delete(`/rooms/${room.id}`);
      toast.success("Room deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="rooms-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Room Inventory</h1>
          <p className="text-sm text-muted-foreground">Manage rooms, rates and housekeeping status.</p>
        </div>
        <Button onClick={openCreate} data-testid="add-room-button" className="transition-colors">
          <Plus size={16} className="mr-2" /> Add room
        </Button>
      </div>

      {rooms && rooms.length > 0 && <RoomTypePhotos rooms={rooms} />}

      {!rooms ? (
        <div className="flex h-40 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {rooms.map((room, i) => (
            <div key={room.id} data-testid={`room-card-${room.id}`}
              className="group overflow-hidden border border-border bg-card transition-shadow hover:shadow-sm">
              <div className="relative h-36 overflow-hidden">
                <img src={ROOM_IMAGES[i % 2]} alt={`Room ${room.name}`}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute right-2 top-2"><StatusBadge status={room.status} /></div>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="font-heading text-xl font-bold" data-testid={`room-name-${room.id}`}>Room {room.name}</div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{room.type}</div>
                </div>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="font-semibold text-primary" data-testid={`room-rate-${room.id}`}>{fmt(room.rate)} <span className="text-xs font-normal text-muted-foreground">/ night</span></span>
                  <span className="flex items-center gap-1 text-muted-foreground"><Users size={14} /> {room.capacity}</span>
                </div>
                {room.auto_relist && (
                  <div className="mt-2 flex items-center gap-1 text-[11px] font-medium text-status-confirmed" data-testid={`auto-relist-badge-${room.id}`}>
                    <ArrowsClockwise size={12} weight="bold" /> Auto-relist on vacancy
                  </div>
                )}
                {easystayConnected && room.listed_status === "listed" && room.sync_status === "synced" && (
                  <div className="mt-2">
                    <span className="inline-flex items-center gap-1 border border-status-confirmed/40 bg-status-confirmed/10 px-2 py-0.5 text-[11px] font-medium text-status-confirmed"
                      data-testid={`listed-easystay-badge-${room.id}`}>
                      <CheckCircle size={12} weight="bold" /> Listed on EasyStay
                    </span>
                  </div>
                )}
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(room)} data-testid={`edit-room-${room.id}`} className="flex-1 transition-colors hover:border-primary">
                    <PencilSimple size={14} className="mr-1.5" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => remove(room)} data-testid={`delete-room-${room.id}`}
                    className="transition-colors hover:border-destructive hover:text-destructive">
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="room-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? `Edit Room ${editing.name}` : "Add a room"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Room number / name</Label>
                <Input data-testid="room-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="room-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(form.type && !TYPES.includes(form.type) ? [...TYPES, form.type] : TYPES).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Nightly rate (ZMW)</Label>
                <Input type="number" min="1" step="0.01" data-testid="room-form-rate" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label>Capacity (guests)</Label>
                <Input type="number" min="1" data-testid="room-form-capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} required />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="room-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Description (optional)</Label>
              <Input data-testid="room-form-description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>EasyStay room/unit ID (optional)</Label>
              <Input data-testid="room-form-external-id" value={form.external_id || ""} onChange={(e) => setForm({ ...form, external_id: e.target.value })} placeholder="Maps this room to an EasyStay unit" />
            </div>
            {form.easystay_listing_id && (
              <div className="flex items-center justify-between gap-3 border border-border bg-muted/40 px-3 py-2" data-testid="room-form-easystay-listing-id">
                <div className="min-w-0">
                  <div className="text-[11px] font-medium text-muted-foreground">Confirmed EasyStay listing ID</div>
                  <div className="truncate font-mono text-xs">{form.easystay_listing_id}</div>
                </div>
                <Button type="button" size="sm" variant="ghost" data-testid="room-form-copy-listing-id"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(form.easystay_listing_id);
                      toast.success("Listing ID copied");
                    } catch {
                      toast.error("Copy blocked by the browser — select the ID and copy it manually");
                    }
                  }}>
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
              </div>
            )}
            {editing && !form.easystay_listing_id && easystayConnected && (
              <p className="text-[11px] text-muted-foreground" data-testid="room-form-not-confirmed">
                Not confirmed on EasyStay yet — relist this room to receive its listing ID.
              </p>
            )}
            <div className="flex items-center justify-between border border-border px-4 py-3" data-testid="room-form-auto-relist-row">
              <div>
                <div className="text-sm font-medium">Auto-relist when vacant</div>
                <div className="text-xs text-muted-foreground">Republish on EasyStay automatically when a booking ends or is cancelled. Off = appears in Vacant Properties for manual relisting.</div>
              </div>
              <Switch checked={!!form.auto_relist} onCheckedChange={(v) => setForm({ ...form, auto_relist: v })} data-testid="room-form-auto-relist" />
            </div>
            <Button type="submit" disabled={saving} data-testid="room-form-submit" className="w-full transition-colors">
              {saving ? "Saving…" : editing ? "Save changes" : "Add room"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
